import { createContext, createElement, type ReactNode, useContext, useRef } from "react";
import { useLoadDurationClasses } from "../../../../http/lock_calculator/useLoadDurationClasses";
import { useMaterialGrades } from "../../../../http/lock_calculator/useMaterialGrades";
import { useServiceClasses } from "../../../../http/lock_calculator/useServiceClasses";
import { useStandards } from "../../../../http/lock_calculator/useStandards";

/** Owns calculator catalog queries and exposes a single read model to features. */
const useCatalogQueries = (isAdmin: boolean, calculationId?: string) => {
  const aborter = useRef(new AbortController());
  const query = { limit: 200, page: 1, q: "" };
  const standardsQuery = useStandards(query, aborter.current.signal, isAdmin);
  const serviceClassesQuery = useServiceClasses(query, aborter.current.signal, isAdmin);
  const loadDurationClassesQuery = useLoadDurationClasses(query, aborter.current.signal, isAdmin);
  const materialGradesQuery = useMaterialGrades(query, aborter.current.signal, isAdmin);

  return {
    isAdmin,
    calculationId,
    standards: standardsQuery.data?.items ?? [],
    serviceClasses: serviceClassesQuery.data?.items ?? [],
    loadDurationClasses: loadDurationClassesQuery.data?.items ?? [],
    materialGrades: materialGradesQuery.data?.items ?? [],
    isLoadingStandards: standardsQuery.isLoading,
    isLoadingServiceClasses: serviceClassesQuery.isLoading,
    isLoadingLoadDurationClasses: loadDurationClassesQuery.isLoading,
    isLoadingMaterialGrades: materialGradesQuery.isLoading,
    standardsError: standardsQuery.error,
    serviceClassesError: serviceClassesQuery.error,
    loadDurationClassesError: loadDurationClassesQuery.error,
  };
};

type CatalogValue = ReturnType<typeof useCatalogQueries>;
const CatalogContext = createContext<CatalogValue | null>(null);

export const LockCalculatorCatalogProvider = ({
  isAdmin,
  calculationId,
  children,
}: {
  isAdmin: boolean;
  calculationId?: string;
  children: ReactNode;
}) => createElement(CatalogContext.Provider, { value: useCatalogQueries(isAdmin, calculationId) }, children);

export const useLockCalculatorCatalog = () => {
  const catalog = useContext(CatalogContext);
  if (!catalog) throw new Error("useLockCalculatorCatalog must be used inside LockCalculatorCatalogProvider");
  return catalog;
};
